import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    RadioGroup,
    FormControlLabel,
    Radio
} from '@mui/material';
import {
    ArrowUpRight,
    ArrowDownRight,
    Save
} from 'lucide-react';

// Trigger type options
const triggerTypes = [
    { value: 'change', label: 'Any Change' },
    { value: 'threshold', label: 'Threshold Value' },
    { value: 'edge', label: 'Edge Trigger' }
];

// Operator options
const triggerOperators = [
    { value: 'equals', label: 'Equals (=)', icon: '=' },
    { value: 'notEquals', label: 'Not Equals (≠)', icon: '≠' },
    { value: 'greaterThan', label: 'Greater Than (>)', icon: '>' },
    { value: 'lessThan', label: 'Less Than (<)', icon: '<' },
    { value: 'greaterThanOrEqual', label: 'Greater or Equal (≥)', icon: '≥' },
    { value: 'lessThanOrEqual', label: 'Less or Equal (≤)', icon: '≤' }
];

const TriggerConditionsForm = ({ 
    initialConditions, 
    onApply,
    disabled = false 
}) => {
    // Local state for form values
    const [localConditions, setLocalConditions] = useState(initialConditions);
    
    // Update local state when initial conditions change
    useEffect(() => {
        setLocalConditions(initialConditions);
    }, [initialConditions]);

    const handleApply = () => {
        const normalizedConditions = {
            ...localConditions,
            triggerType: localConditions.triggerType.toLowerCase(),
            operator: localConditions.operator.toLowerCase(),
            thresholdValue: localConditions.thresholdValue?.toString() || '',
            risingEdge: Boolean(localConditions.risingEdge)
        };
        
        console.log('Applying normalized conditions:', normalizedConditions);
        onApply(normalizedConditions);
    };

    return (
        <Box sx={{ mt: 3, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
                Trigger Conditions
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Trigger Type</InputLabel>
                <Select
                    value={localConditions.triggerType}
                    label="Trigger Type"
                    disabled={disabled}
                    onChange={(e) => setLocalConditions(prev => ({
                        ...prev,
                        triggerType: e.target.value,
                        // Reset other values when type changes
                        operator: 'equals',
                        thresholdValue: '',
                        risingEdge: true
                    }))}
                >
                    {triggerTypes.map(type => (
                        <MenuItem key={type.value} value={type.value}>
                            {type.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {localConditions.triggerType === 'threshold' && (
                <>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Comparison Operator</InputLabel>
                        <Select
                            value={localConditions.operator}
                            label="Comparison Operator"
                            disabled={disabled}
                            onChange={(e) => setLocalConditions(prev => ({
                                ...prev,
                                operator: e.target.value
                            }))}
                        >
                            {triggerOperators.map(op => (
                                <MenuItem key={op.value} value={op.value}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography sx={{ fontFamily: 'monospace' }}>{op.icon}</Typography>
                                        {op.label}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        label="Threshold Value"
                        type="number"
                        disabled={disabled}
                        value={localConditions.thresholdValue}
                        onChange={(e) => setLocalConditions(prev => ({
                            ...prev,
                            thresholdValue: e.target.value
                        }))}
                    />
                </>
            )}

            {localConditions.triggerType === 'edge' && (
                <FormControl component="fieldset" disabled={disabled}>
                    <RadioGroup
                        value={localConditions.risingEdge ? 'rising' : 'falling'}
                        onChange={(e) => setLocalConditions(prev => ({
                            ...prev,
                            risingEdge: e.target.value === 'rising'
                        }))}
                    >
                        <FormControlLabel 
                            value="rising" 
                            control={<Radio />} 
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ArrowUpRight size={16} />
                                    <span>Rising Edge (0 → 1)</span>
                                </Box>
                            }
                        />
                        <FormControlLabel 
                            value="falling" 
                            control={<Radio />} 
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ArrowDownRight size={16} />
                                    <span>Falling Edge (1 → 0)</span>
                                </Box>
                            }
                        />
                    </RadioGroup>
                </FormControl>
            )}

            {/* SELECT button only shows when there are additional settings to confirm */}
            {(localConditions.triggerType === 'threshold' || localConditions.triggerType === 'edge') && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        disabled={disabled}
                        onClick={handleApply}
                        sx={{
                            bgcolor: '#edbc45',
                            color: '#000',
                            fontWeight: 600,
                            '&:hover': { bgcolor: '#d4a03a' }
                        }}
                    >
                        Select
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default TriggerConditionsForm;